import { useState } from 'react'
import { listSubscriptions, deleteSubscription, runSubscriptions, type Subscription } from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useAuth } from '../layout/AuthContext'
import { useToast } from '../layout/ToastContext'
import { brandName, formatMoney, BRANDS, subscriptionStatusLabel } from '../lookups'
import { fill, SUBSCRIPTION_STATUS_LABELS } from '../../shared/texts'
import { useServerList } from '../hooks/useServerList'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ColumnManager from '../components/ColumnManager'
import ConfirmDialog from '../components/ConfirmDialog'
import { useBulkTrash } from '../components/BulkTrash'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { PAGES_SUBSCRIPTIONS_LIST_TEXTS as T } from '../../shared/texts'

// Original icons from references/ (served from public/icons/).
const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />

// Aus der einen Tabelle gebaut — siehe den Kommentar dort.
const STATUSES = Object.entries(SUBSCRIPTION_STATUS_LABELS).map(([key, name]) => ({ key, name }))
const CADENCES = [
    { key: 'monthly', name: T.monthly },
    { key: 'yearly', name: T.yearly },
]

export default function SubscriptionsList() {
    const { toast } = useToast()
    const { openTab, publishChange } = useTabs()
    // 'customers': the customer column shows the name, which is edited elsewhere.
    const version = useDataVersion('subscriptions') + useDataVersion('customers')
    const { preferences, savePreferences, can } = useAuth()
    const canWrite = can('subscriptions', 'write')
    const canDelete = can('subscriptions', 'delete')

    const [status, setStatus] = useState('')
    const [brand, setBrand] = useState('')
    const [cadence, setCadence] = useState('')
    const list = useServerList<Subscription>(listSubscriptions, version, {
        sort: 'createdAt', dir: 'desc', pageSize: preferences.tablePageSize?.subscriptions ?? DEFAULT_TABLE_PAGE_SIZE,
        params: { status: status || undefined, brand: brand || undefined, cadence: cadence || undefined },
    })
    const cols = useColumnVisibility('subscriptions')

    const onPageSize = (n: number) => {
        list.setPageSize(n)
        savePreferences({ tablePageSize: { ...(preferences.tablePageSize ?? {}), subscriptions: n } })
    }

    const customerLabel = (s: Subscription) => s.customerName || s.customerId

    const openNew = () => openTab({ id: 'subscription-new', name: T.newTab, component: 'subscription-form' })
    const openEdit = (s: Subscription) => openTab({
        id: `subscription-${s._id}`,
        name: fill(T.editTab, { customer: customerLabel(s) }),
        component: 'subscription-form',
        config: { id: s._id },
    })

    // Delete (soft) confirmation: the targeted abo (null = dialog closed) + state.
    const [toDelete, setToDelete] = useState<Subscription | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')
    const askDelete = (s: Subscription) => { setDelError(''); setToDelete(s) }
    const cancelDelete = () => { if (!delBusy) { setToDelete(null); setDelError('') } }
    async function confirmDelete() {
        if (!toDelete) return
        setDelBusy(true); setDelError('')
        try {
            await deleteSubscription(toDelete._id)
            publishChange('subscriptions')
            toast(T.trashed)
            setToDelete(null)
        } catch (e: any) {
            setDelError(e?.response?.data?.error ?? T.deleteFailed)
        } finally {
            setDelBusy(false)
        }
    }

    const [runBusy, setRunBusy] = useState(false)
    async function onRun() {
        setRunBusy(true)
        try {
            const r = await runSubscriptions()
            toast(fill(T.runDone, { count: r.count })
                + (r.errors.length ? fill(T.runErrors, { n: r.errors.length }) : '.'))
            publishChange('subscriptions') // own list reloads via version bump
            publishChange('invoices')      // a run may have created invoices
        } catch (e: any) {
            toast(e?.response?.data?.error ?? e?.message ?? T.runFailed)
        } finally { setRunBusy(false) }
    }

    // Mehrfachauswahl → Papierkorb (components/BulkTrash.tsx): same right as the single delete.
    const bulk = useBulkTrash<Subscription>({
        kind: 'subscriptions', enabled: canDelete, rows: list.rows, rowId: (r) => r._id, total: list.total,
        filter: { q: list.q, status: status || undefined, brand: brand || undefined, cadence: cadence || undefined },
        onDone: () => publishChange('subscriptions'),
    })

    const columns: Column<Subscription>[] = [
        { key: 'customer', header: T.colCustomer, sortField: 'customer', defaultWidth: 160, render: (s) => customerLabel(s) },
        { key: 'brand', header: T.brand, sortField: 'brand', defaultWidth: 120, render: (s) => brandName(s.brand) },
        { key: 'cadence', header: T.cadence, sortField: 'cadence', defaultWidth: 90, render: (s) => s.cadence === 'monthly' ? T.monthly : T.yearly },
        // The placeholders stay visible here — this is the template, not an invoice line.
        { key: 'product', header: T.colProduct, defaultWidth: 220, render: (s) => (s.product ?? '').split('\n')[0] || '—' },
        {
            key: 'netPrice', header: T.colNetPrice, sortField: 'netPrice', className: 'num', defaultWidth: 120,
            render: (s) => (s.netPrice == null ? '—' : formatMoney(s.netPrice, s.currency)),
        },
        { key: 'mode', header: T.colMode, className: 'center', defaultWidth: 70, render: (s) => s.mode },
        // Klasse = Rohwert (Farbe), Text = Label. Wie in der Rechnungsliste.
        { key: 'status', header: T.status, sortField: 'status', className: 'center', defaultWidth: 90, render: (s) => <span className={`badge ${s.status}`}>{subscriptionStatusLabel(s.status)}</span> },
        // `nextRunAt` is OUR scheduler's date, and the scheduler skips PSP-driven subscriptions
        // (source $nin ['stripe','mollie'] — no double billing). The field is set on those rows
        // anyway and was shown as "Nächster Lauf", i.e. a date on which nothing whatsoever
        // happens. Say who runs it instead of printing a number that means nothing.
        {
            key: 'nextRunAt', header: T.colNextBilling, sortField: 'nextBillingDate', defaultWidth: 150,
            render: (s) => {
                if (s.source === 'stripe' || s.source === 'mollie') {
                    const psp = s.source === 'mollie' ? T.pspMollie : T.pspStripe
                    return s.currentPeriodEnd
                        ? <span className="hint">{fill(T.viaPspPaidUntil, { psp, date: new Date(s.currentPeriodEnd).toLocaleDateString('de-DE') })}</span>
                        : <span className="hint">{fill(T.viaPsp, { psp })}</span>
                }
                // Manual abos: the next period to bill (month-end run). A failed run keeps the date
                // and says so — the operator must see it without opening every abo.
                const date = s.nextBillingDate ? new Date(s.nextBillingDate).toLocaleDateString('de-DE', { timeZone: 'UTC' }) : '—'
                return s.lastRunError
                    ? <span className="badge err" title={s.lastRunError}>{fill(T.runError, { date })}</span>
                    : date
            },
        },
        {
            key: 'actions', header: T.colActions, defaultWidth: 100, sticky: true,
            render: (s) => (
                <div className="row-actions">
                    <button className="icon-action" title={canWrite ? T.edit : T.view} aria-label={T.edit} onClick={() => openEdit(s)}><EditIcon /></button>
                    {canDelete && <button className="icon-action" title={T.delete} aria-label={T.delete} onClick={() => askDelete(s)}><TrashIcon /></button>}
                </div>
            ),
        },
    ]
    if (bulk.column) columns.unshift(bulk.column)

    const filtered = list.q.trim() !== '' || status !== '' || brand !== '' || cadence !== ''

    return (
        <div className="list-page">
            <ListToolbar
                title={T.title}
                infoText={T.info}
                action={canWrite ? (
                    <div className="row">
                        <button className="secondary" disabled={runBusy} onClick={onRun}>
                            {runBusy ? '…' : T.runDue}
                        </button>
                        <button onClick={openNew}>{T.newSubscription}</button>
                    </div>
                ) : undefined}
            />

            {list.error && <div className="notice err">{list.error}</div>}

            {bulk.bar}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={filtered}
                search={{ value: list.q, onChange: list.setQ }}
                columnManager={<ColumnManager columns={columns} hidden={cols.hidden} onToggle={cols.toggle} onReset={cols.reset} />}
                filters={<>
                    <label>
                        {T.status}
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">{T.all}</option>
                            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                        </select>
                    </label>
                    <label>
                        {T.brand}
                        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                            <option value="">{T.all}</option>
                            {BRANDS.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                        </select>
                    </label>
                    <label>
                        {T.cadence}
                        <select value={cadence} onChange={(e) => setCadence(e.target.value)}>
                            <option value="">{T.all}</option>
                            {CADENCES.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                        </select>
                    </label>
                </>}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.empty}</p>
                ) : (
                    <DataTable
                        key={cols.resetKey}
                        tableId="subscriptions"
                        columns={columns}
                        hidden={cols.hidden}
                        rows={list.rows}
                        rowKey={(s) => s._id}
                        sort={list.sort}
                        dir={list.dir}
                        onSort={list.toggleSort}
                        onRowActivate={openEdit}
                    />
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={onPageSize} />

            <ConfirmDialog
                open={!!toDelete}
                title={T.trashTitle}
                message={toDelete
                    ? <>{fill(T.trashQuestion, { customer: customerLabel(toDelete) })}<br />{T.trashAdminsOnly}</>
                    : null}
                confirmLabel={T.trashTitle}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
            {bulk.dialogs}
        </div>
    )
}
